import { RefrigerationState } from 'src/graphql/types/delivery';

// Minimal order-line shape needed for sorting. Generic over the concrete line
// type so any query's order-line (delivery, review detail, …) can be sorted as
// long as it carries these sku fields.
type SortableOrderLine = {
  sku: { refrigerationState: string; categoryName?: string; name?: string };
};

const getRefrigerationPriority = (refrigerationState: string): number => {
  switch (refrigerationState) {
    case RefrigerationState.frozen:
      return 0; // Highest priority
    case RefrigerationState.refrigerated:
      return 1; // Second priority
    default:
      return 2; // Lowest priority
  }
};

const getCategoryPriority = (categoryName: string | undefined): number => {
  if (categoryName === 'Alternative Milk') {
    return 0; // Highest priority
  }
  return 1; // All other categories
};

export const sortOrderLines = <T extends SortableOrderLine>(
  orderLines: T[],
): T[] => {
  return [...orderLines].sort((a, b) => {
    // First sort by refrigeration state (frozen -> refrigerated -> other)
    const refrigerationPriorityA = getRefrigerationPriority(
      a.sku.refrigerationState,
    );
    const refrigerationPriorityB = getRefrigerationPriority(
      b.sku.refrigerationState,
    );

    if (refrigerationPriorityA !== refrigerationPriorityB) {
      return refrigerationPriorityA - refrigerationPriorityB;
    }

    // Then sort by category (Alternative Milk first, then everything else)
    const categoryPriorityA = getCategoryPriority(a.sku.categoryName);
    const categoryPriorityB = getCategoryPriority(b.sku.categoryName);

    if (categoryPriorityA !== categoryPriorityB) {
      return categoryPriorityA - categoryPriorityB;
    }

    // Finally, sort alphabetically by item name for consistent ordering
    const itemNameA = a.sku.name || '';
    const itemNameB = b.sku.name || '';
    return itemNameA.localeCompare(itemNameB);
  });
};
